const FAVOURITES_CONFIG = {
    LOCAL_STORAGE_KEY_PREFIX: 'favourites',
    LEGACY_KEY: 'my-favourites',
    DEBOUNCE_DELAY: 1000,
    NOTIFICATION_DURATION: 3000,
    ENDPOINTS: {
        ADD: '/apps/bat-enhanced/customer/favourites/add',
        REMOVE: '/apps/bat-enhanced/customer/favourites/remove',
        SYNC: '/apps/bat-enhanced/customer/favourites/sync',
    },
};

const FavouritesUtils = {
    createStorageKey(handle, id) {
        return id ? `${handle}/${id}` : handle;
    },

    extractHandle(key) {
        return key.split('/')[0];
    },

    extractId(key) {
        const parts = key.split('/');
        return parts.length > 1 ? parts[1] : null;
    },

    isFavourite(favourites, handle, id) {
        if (id) {
            return favourites.some((fav) => {
                const favId = this.extractId(fav);
                return favId === id;
            });
        }
        return favourites.some((fav) => {
            const favHandle = this.extractHandle(fav);
            return favHandle === handle;
        });
    },

    removeFavourite(favourites, handle, id) {
        if (id) {
            return favourites.filter((fav) => {
                const favId = this.extractId(fav);
                return favId !== id;
            });
        }
        return favourites.filter((fav) => {
            const favHandle = this.extractHandle(fav);
            return favHandle !== handle;
        });
    },
};

class StorageManager {
    constructor() {
        this.country = window.Shopify?.country || 'DEFAULT';
        this.storageKey = this.getStorageKey();
        this.migrateLegacyData();
    }

    getStorageKey() {
        return `${FAVOURITES_CONFIG.LOCAL_STORAGE_KEY_PREFIX}_${this.country}`;
    }

    migrateLegacyData() {
        try {
            const currentData = localStorage.getItem(this.storageKey);
            if (currentData) {
                return;
            }

            const legacyData = localStorage.getItem(FAVOURITES_CONFIG.LEGACY_KEY);
            if (legacyData) {
                localStorage.setItem(this.storageKey, legacyData);
                localStorage.removeItem(FAVOURITES_CONFIG.LEGACY_KEY);
                console.log(`Migrated favourites to ${this.country}-specific storage`);
                return;
            }

            const simpleLegacyData = localStorage.getItem('favourites');
            if (simpleLegacyData) {
                localStorage.setItem(this.storageKey, simpleLegacyData);
                localStorage.removeItem('favourites');
                console.log(`Migrated favourites from simple key to ${this.country}-specific storage`);
            }
        } catch (error) {
            console.error('Error during favourites migration:', error);
        }
    }

    getAllCountryFavourites() {
        const allFavourites = {};
        const prefix = FAVOURITES_CONFIG.LOCAL_STORAGE_KEY_PREFIX + '_';

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
                const country = key.replace(prefix, '');
                try {
                    allFavourites[country] = JSON.parse(localStorage.getItem(key));
                } catch (e) {
                    console.error(`Error parsing favourites for ${country}:`, e);
                }
            }
        }

        return allFavourites;
    }

    get() {
        try {
            const data = localStorage.getItem(this.storageKey);
            return data ? JSON.parse(data) : [];
        } catch (error) {
            console.error('Error reading favourites from localStorage:', error);
            return [];
        }
    }

    set(data) {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(data));
        } catch (error) {
            console.error('Error saving favourites to localStorage:', error);
        }
    }

    cleanupLegacyData() {}
}

class FavouritesService {
    constructor() {
        this.storageManager = new StorageManager();
        this.favouriteProducts = this.storageManager.get();
        this.pendingChanges = [];
        this.country = window.Shopify?.country || 'DEFAULT';
    }

    getFromLocalStorage() {
        return this.storageManager.get();
    }

    saveToLocalStorage(data) {
        this.favouriteProducts = data;
        this.storageManager.set(data);
    }

    async makeRequest(endpoint, payload) {
        const response = await fetch(getGlobalDomain(endpoint), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`Request failed: ${response.status}`);
        }

        return response.json();
    }

    async syncWithServer() {
        const itemsToRemove = this.pendingChanges.filter((item) => item.action === 'remove');
        const itemsToAdd = this.pendingChanges.filter((item) => item.action === 'add');
        let responseItems = this.favouriteProducts;

        if (itemsToRemove.length > 0) {
            const keysToRemove = itemsToRemove.map((item) => FavouritesUtils.createStorageKey(item.handle, item.id));
            const data = await this.makeRequest(FAVOURITES_CONFIG.ENDPOINTS.REMOVE, keysToRemove);
            if (!data.ok) throw new Error('Failed to remove favourites');
            responseItems = data.productHandles;
        }

        if (itemsToAdd.length > 0) {
            const keysToAdd = itemsToAdd.map((item) => FavouritesUtils.createStorageKey(item.handle, item.id));
            const data = await this.makeRequest(FAVOURITES_CONFIG.ENDPOINTS.ADD, keysToAdd);
            if (!data.ok) throw new Error('Failed to add favourites');
            responseItems = data.productHandles;
        }

        this.saveToLocalStorage(responseItems);
        this.pendingChanges = [];

        return responseItems;
    }

    async syncInitialFavourites() {
        try {
            const data = await this.makeRequest(FAVOURITES_CONFIG.ENDPOINTS.SYNC, { handles: this.favouriteProducts });

            if (!data.ok) throw new Error('Error syncing favorites');

            this.saveToLocalStorage(data.productHandles);

            console.log(`Favourites synced for ${this.country} store`);
        } catch (error) {
            console.error('Failed to sync favorites:', error);
        }
    }

    applyLocalChanges() {
        let updatedFavourites = [...this.favouriteProducts];

        this.pendingChanges.forEach((item) => {
            const storageKey = FavouritesUtils.createStorageKey(item.handle, item.id);

            if (item.action === 'add') {
                const exists = FavouritesUtils.isFavourite(updatedFavourites, item.handle, item.id);
                if (!exists) {
                    updatedFavourites.push(storageKey);
                }
            } else if (item.action === 'remove') {
                updatedFavourites = FavouritesUtils.removeFavourite(updatedFavourites, item.handle, item.id);
            }
        });

        this.saveToLocalStorage(updatedFavourites);
        this.pendingChanges = [];
        return updatedFavourites;
    }

    addChange(change) {
        this.pendingChanges = this.pendingChanges.filter((item) => {
            if (change.id && item.id) {
                return item.id !== change.id;
            }
            return item.handle !== change.handle;
        });
        this.pendingChanges.push(change);
    }

    hasPendingChanges() {
        return this.pendingChanges.length > 0;
    }

    isFavourite(handle, id) {
        return FavouritesUtils.isFavourite(this.favouriteProducts, handle, id);
    }
}

class NotificationManager {
    constructor() {
        this.notificationElement = null;
        this.strings = window.favouriteStrings || {};
    }

    show(title, message, actionKey) {
        const notificationElement = this.getNotificationElement();
        if (!notificationElement) {
            console.error('FavouritesNotification component not found.');
            return;
        }

        const productNameEl = notificationElement.querySelector('.product__name');
        const actionEl = notificationElement.querySelector('.favourites__action');

        if (productNameEl) {
            productNameEl.classList.toggle('hidden', !title);
            productNameEl.textContent = title || '';
        }

        if (actionEl) {
            const actionMessage = message || this.strings[actionKey] || '';
            actionEl.textContent = actionMessage;
        }

        notificationElement.classList.add('favourites-notification--active');

        setTimeout(() => {
            notificationElement.classList.remove('favourites-notification--active');
        }, FAVOURITES_CONFIG.NOTIFICATION_DURATION);
    }

    showForChanges(changes) {
        if (changes.length === 0) return;

        if (changes.length === 1) {
            const change = changes[0];
            this.show(change.title, undefined, change.action);
        } else {
            const adds = changes.filter((item) => item.action === 'add');
            const removes = changes.filter((item) => item.action === 'remove');

            if (adds.length > 0 && removes.length > 0) {
                this.show(undefined, this.strings.multipleItemsChanged);
            } else if (adds.length > 0) {
                this.show(undefined, this.strings.multipleItemsAdded);
            } else if (removes.length > 0) {
                this.show(undefined, this.strings.multipleItemsRemoved);
            }
        }
    }

    getNotificationElement() {
        if (!this.notificationElement) {
            this.notificationElement = document.querySelector('.favourites-notification');
        }
        return this.notificationElement;
    }
}

class FavouritesController {
    constructor() {
        this.service = new FavouritesService();
        this.notificationManager = new NotificationManager();
        this.isCustomer = window.customerVariables?.isCustomer || false;

        this.debouncedSync = debounce(this.handleSync.bind(this), FAVOURITES_CONFIG.DEBOUNCE_DELAY);

        this.init();
    }

    async init() {
        if (this.isCustomer) {
            await this.service.syncInitialFavourites();
        }
    }

    async handleSync() {
        if (!this.service.hasPendingChanges()) return;

        const changesToProcess = [...this.service.pendingChanges];

        try {
            if (this.isCustomer) {
                await this.service.syncWithServer();
            } else {
                this.service.applyLocalChanges();
            }

            this.notificationManager.showForChanges(changesToProcess);
            this.dispatchUpdateEvent();
        } catch (error) {
            console.error('Error syncing favourites:', error);
        }
    }

    toggleFavourite(handle, title, id) {
        const isFavourite = this.service.isFavourite(handle, id);
        const action = isFavourite ? 'remove' : 'add';

        this.service.addChange({ action, handle, title, id });
        this.debouncedSync();

        return !isFavourite;
    }

    getFavourites() {
        return this.service.favouriteProducts;
    }

    isFavourite(handle, id) {
        return this.service.isFavourite(handle, id);
    }

    dispatchUpdateEvent() {
        document.dispatchEvent(
            new CustomEvent('favourites:updated', {
                bubbles: true,
                composed: true,
                detail: { favourites: this.service.favouriteProducts },
            })
        );
    }
}

const favouritesController = new FavouritesController();

class FavouritesWidget extends HTMLElement {
    constructor() {
        super();
        this.setupProperties();
        this.setupElements();
        this.setupEventListeners();
        this.initializeState();
    }

    setupProperties() {
        this.productHandle = this.dataset.handle;
        this.productTitle = this.dataset.title;
        this.productId = this.dataset.id;
        this.isLoading = false;

        this.strings = {
            ariaAdd: window.favouriteStrings?.ariaAdd || 'Add [product] to favorites',
            ariaRemove: window.favouriteStrings?.ariaRemove || 'Remove [product] from favorites',
            announceAdd: window.favouriteStrings?.announceAdd || '[product] added to favorites',
            announceRemove: window.favouriteStrings?.announceRemove || '[product] removed from favorites',
        };
    }

    setupElements() {
        this.favouriteIcon = this.querySelector('.favourites__icon');
        this.favouriteSpinner = this.querySelector('.favourites__spinner');
        this.srStatus = this.querySelector('.favourites__sr-status');
    }

    setupEventListeners() {
        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('keydown', this.handleKeydown.bind(this));

        document.addEventListener('favourites:updated', () => {
            if (this.isLoading) {
                this.setLoading(false);
            }
        });
    }

    initializeState() {
        const isFavourite = favouritesController.isFavourite(this.productHandle, this.productId);
        this.updateUI(isFavourite);
    }

    handleClick(event) {
        event.preventDefault();
        this.toggleFavorite();
    }

    handleKeydown(event) {
        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            this.toggleFavorite();
        }
    }

    toggleFavorite() {
        const currentState = this.favouriteIcon.classList.contains('favourites__icon--active');
        const newState = !currentState;

        this.updateUI(newState);
        this.setLoading(true);
        this.announceToScreenReader(newState);

        favouritesController.toggleFavourite(this.productHandle, this.productTitle, this.productId);
    }

    updateUI(isFavourite) {
        this.favouriteIcon?.classList.toggle('favourites__icon--active', isFavourite);
        this.setAttribute('aria-pressed', String(isFavourite));
        this.setAttribute('aria-label', this.getAriaLabel(isFavourite));
    }

    setLoading(loading) {
        this.isLoading = loading;
        this.favouriteSpinner?.classList.toggle('hidden', !loading);
        this.favouriteIcon?.classList.toggle('hidden', loading);
    }

    getAriaLabel(isFavourite) {
        const template = isFavourite ? this.strings.ariaRemove : this.strings.ariaAdd;
        return template.replace('[product]', this.productTitle);
    }

    announceToScreenReader(isFavourite) {
        if (!this.srStatus) return;

        const template = isFavourite ? this.strings.announceAdd : this.strings.announceRemove;
        const message = template.replace('[product]', this.productTitle);

        this.srStatus.textContent = '';
        requestAnimationFrame(() => {
            this.srStatus.textContent = message;
        });
    }
}

class MainFavourites extends HTMLElement {
    constructor() {
        super();
        this.setupElements();
        this.setupEventListeners();
        this.initPage();
    }

    setupElements() {
        this.emptyState = this.querySelector('.favourites-products__empty');
        this.grid = this.querySelector('#favouritesGrid');
        this.loadingSpinner = document.querySelector('.global-loading');
    }

    setupEventListeners() {
        document.addEventListener('favourites:updated', this.handleFavouritesUpdate.bind(this));
    }

    async initPage() {
        this.showLoading(true);

        const favourites = favouritesController.getFavourites();

        if (favourites.length > 0) {
            await this.renderFavourites(favourites);
            this.showEmptyState(false);
        } else {
            this.showEmptyState(true);
        }

        this.showLoading(false);
    }

    async handleFavouritesUpdate(event) {
        const favourites = favouritesController.getFavourites();

        this.removeUnfavouritedItems(favourites);

        if (favourites.length === 0) {
            this.showEmptyState(true);
        }
    }

    removeUnfavouritedItems(currentFavourites) {
        if (!this.grid) return;

        const widgets = this.grid.querySelectorAll('favourites-widget');

        widgets.forEach((widget) => {
            const handle = widget.dataset.handle;
            const id = widget.dataset.id;

            const stillFavourite = FavouritesUtils.isFavourite(currentFavourites, handle, id);

            if (!stillFavourite) {
                const gridItem = widget.closest('.grid__item');
                if (gridItem) {
                    gridItem.style.transition = 'opacity 0.3s ease-out';
                    gridItem.style.opacity = '0';

                    setTimeout(() => {
                        gridItem.remove();

                        if (this.grid.children.length === 0) {
                            this.showEmptyState(true);
                        }
                    }, 300);
                }
            }
        });
    }

    async renderFavourites(favourites) {
        if (!this.grid) return;

        const handles = favourites.map((fav) => FavouritesUtils.extractHandle(fav));

        const productCards = await Promise.all(handles.map((handle) => this.fetchProductCard(handle)));

        const unavailableHandles = productCards
            .filter((response) => response.status !== 200)
            .map((response) => response.handle);

        const redirectedHandles = productCards
            .filter((response) => response.status === 200 && response.wasRedirected)
            .map((response) => ({ oldHandle: response.handle, newHandle: response.actualHandle }));

        const availableCards = productCards
            .filter((response) => response.status === 200)
            .map((response) => response.body);

        if (unavailableHandles.length > 0) {
            await this.removeSilently(unavailableHandles);
        }

        if (redirectedHandles.length > 0) {
            await this.updateRedirectedHandles(redirectedHandles);
        }

        this.grid.innerHTML = availableCards.filter(Boolean).join('');
        this.grid.classList.remove('hidden');

        const quickAdd = document.querySelector('quick-add-drawer');
        quickAdd?.setQuickAddLinkClicks?.();
    }

    async removeSilently(handles) {
        const updatedFavourites = favouritesController.service.favouriteProducts.filter(
            (handle) => !handles.includes(handle)
        );
        favouritesController.service.saveToLocalStorage(updatedFavourites);

        if (favouritesController.isCustomer && handles.length > 0) {
            try {
                await favouritesController.service.makeRequest(FAVOURITES_CONFIG.ENDPOINTS.REMOVE, handles);
            } catch (error) {
                console.error('[Favourites] Failed to silently remove unavailable products:', error);
            }
        }
    }

    async updateRedirectedHandles(redirectedHandles) {
        let updatedFavourites = [...favouritesController.service.favouriteProducts];

        redirectedHandles.forEach(({ oldHandle, newHandle }) => {
            const index = updatedFavourites.indexOf(oldHandle);
            if (index !== -1) {
                updatedFavourites[index] = newHandle;
            }
        });

        favouritesController.service.saveToLocalStorage(updatedFavourites);

        if (favouritesController.isCustomer) {
            try {
                const oldHandles = redirectedHandles.map((r) => r.oldHandle);
                const newHandles = redirectedHandles.map((r) => r.newHandle);

                await favouritesController.service.makeRequest(FAVOURITES_CONFIG.ENDPOINTS.REMOVE, oldHandles);

                await favouritesController.service.makeRequest(FAVOURITES_CONFIG.ENDPOINTS.ADD, newHandles);
            } catch (error) {
                console.error('[Favourites] Failed to update redirected handles on server:', error);
            }
        }
    }

    async fetchProductCard(handle) {
        try {
            const requestUrl = getGlobalDomain(`/products/${handle}?view=card&remove=true`);
            const response = await fetch(requestUrl);

            // Sometimes shopify just randomly decides to set redirect to true and
            // returns completely the wrong page. This is to combat that
            if (response.redirected) {
                return {
                    status: 404,
                    handle,
                    actualHandle: handle,
                    wasRedirected: false,
                    body: '',
                };
            }

            const responseUrl = new URL(response.url);
            const requestPath = getGlobalDomain(`/products/${handle}`);
            const responsePath = responseUrl.pathname;

            let actualHandle = handle;
            let wasRedirected = false;

            if (responsePath !== requestPath) {
                const match = responsePath.match(/\/products\/([^/?]+)/);
                if (match) {
                    actualHandle = match[1];
                    wasRedirected = true;
                }
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const productCard = doc.querySelector('.product-card-wrapper');

            if (!productCard)
                return {
                    status: 404,
                    handle,
                    actualHandle: handle,
                    wasRedirected: false,
                    body: '',
                };

            const wrapper = document.createElement('div');
            wrapper.className = 'grid__item';
            wrapper.appendChild(productCard);

            return {
                status: 200,
                handle,
                actualHandle,
                wasRedirected,
                body: wrapper.outerHTML,
            };
        } catch (error) {
            console.error(`[Favourites] Failed to load product card for: ${handle}`, error);
            return {
                status: 404,
                handle,
                actualHandle: handle,
                wasRedirected: false,
                body: '',
            };
        }
    }

    showEmptyState(show) {
        if (this.emptyState) {
            this.emptyState.classList.toggle('hidden', !show);
        }
        if (this.grid && show) {
            this.grid.classList.add('hidden');
        }
    }

    showLoading(show) {
        this.loadingSpinner?.classList.toggle('hidden', !show);
    }
}

customElements.define('favourites-widget', FavouritesWidget);
customElements.define('main-favourites', MainFavourites);
